import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSearchHistory } from '../hooks/useSearchHistory';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableSortLabel,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import { TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useLocalStorage } from '../hooks/useLocalStorage';

const CACHE_EXPIRATION = 24 * 60 * 60 * 1000; // 24 hrs

const AlertTable = (props) => {
  const navigate = useNavigate();
  const { searchHistory } = useSearchHistory();

  const [displayData, setDisplayData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('');
  const isFeedback = props.isFeedback || false;
  const [companyType, setCompanyType] = useState('all'); // 'all', 'converged' or 'non_converged'

  const [cachedData, setCachedData] = useLocalStorage('categoryAlertData', {
    data: null,
    timestamp: null
  });

  const isCacheValid = () => {
    if (!cachedData.timestamp) return false;
    const now = Date.now();
    return (now - cachedData.timestamp) < CACHE_EXPIRATION;
  };

  const detectDataType = (data) => {
    if (!data || data.length === 0) return null;
    const first = data[0];
    if (isFeedback && first.attribute_value !== undefined && first.count !== undefined) {
      return 'feedback';
    }

    if (first.companyName && first.count !== undefined) {
      return 'company';
    }

    if (first.attribute_value && first.count !== undefined) {
      return 'state';
    }

    if (first.category && first.increase_percentage !== undefined) {
      return 'alert';
    }

    if (first.categoryName && first.counts !== undefined) {
      return 'category';
    }

    if (first.companyOfferToConsumer !== undefined && first.total !== undefined) {
      return 'company offers';
    }

    console.warn("⚠️ Unknown data type. First item keys:", Object.keys(first));
    return 'unknown';
  };

  const getValueKey = (type) => {
    switch (type) {
      case 'feedback': return 'count';
      case 'state': return 'doc_count';
      case 'company': return 'count';
      case 'alert': return 'increase_percentage';
      case 'category': return 'counts';
      case 'company offers': return 'total';
      default: return '';
    }
  };

  const getLabelKey = (type) => {
    switch (type) {
      case 'feedback': return 'attribute_value';
      case 'state': return 'key';
      case 'company': return 'companyName';
      case 'alert': return 'category';
      case 'category': return 'categoryName';
      case 'company offers': return 'companyOfferToConsumer';
      default: return '';
    }
  };

  const formatCompanyLabel = (row, type) => {
    if (type === 'company' && row.companyName && row.catName) {
      return `${row.companyName} - ${row.catName}`;
    }
    const labelKey = getLabelKey(type);
    let label = row[labelKey];
    if (type === 'company offers') {
      if (label === null || label === "NULL" || label === "") {
        return "Unspecified";
      }
    }
    return label || 'N/A';
  };

  const getColumnTitle = (type) => {
    switch (type) {
      case 'feedback': return 'Count';
      case 'state': return 'Counts';
      case 'company': return 'Counts';
      case 'alert': return 'Trend';
      case 'category': return 'Counts';
      default: return 'Value';
    }
  };

  // Sorting
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = [...displayData].sort((a, b) => {
    if (!orderBy) return 0;
    if (typeof a[orderBy] === 'string') {
      return order === 'asc'
        ? a[orderBy].localeCompare(b[orderBy])
        : b[orderBy].localeCompare(a[orderBy]);
    } else {
      return order === 'asc' ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy];
    }
  });

  const getAlertColor = (val) => {
    if (Math.abs(val) > 20) return 'error.main';
    if (Math.abs(val) > 10) return 'warning.main';
    return 'success.main';
  };

  // ✅ CRITICAL: Update displayData whenever props.data changes
  useEffect(() => {

    if (props.data && Array.isArray(props.data) && props.data.length > 0) {
      let processedData = props.data;

      if (isFeedback && Array.isArray(props.data)) {
        processedData = props.data.filter(item =>
          item.attribute_value !== null &&
          item.attribute_value !== undefined &&
          item.attribute_value !== ''
        );
      }

      const detectedType = detectDataType(processedData);

      // Pre-process company offers to merge empty fields
      if (detectedType === 'company offers') {
        const mergedOffers = {};

        processedData.forEach(item => {
          let label = item.companyOfferToConsumer;
          if (label === null || label === "NULL" || label === "") {
            label = "Unspecified";
          }

          if (!mergedOffers[label]) {
            mergedOffers[label] = {
              companyOfferToConsumer: label,
              total: 0
            };
          }
          mergedOffers[label].total += (item.total || 0);
        });

        processedData = Object.values(mergedOffers).sort((a, b) => b.total - a.total);
      }

      if (detectedType === 'alert') {
        processedData = processedData.filter(item => item.increase_percentage > 20);
      }

      setDisplayData(processedData);
      setOrderBy(getValueKey(detectedType));
    } else {
      setDisplayData([]);
    }
  }, [props.data, isFeedback]);

  const type = detectDataType(props.data);
  const labelKey = getLabelKey(type);
  const valueKey = getValueKey(type);
  const valueTitle = getColumnTitle(type);

  const getHeaderColor = (title) => {
    if (title && title.startsWith('Company offers')) return '#f2e0abff';
    switch (title) {
      case 'High Alerts': return '#c6e1ff';
      case 'Categories': return '#f5dad6ff';
      case 'Companies': return '#c8f0e6ff';
      case 'States': return '#D0CEFB';
      default: return '#5525E9';
    }
  };

  const getButtonColor = (title) => {
    if (title && title.startsWith('Company offers')) return '#f7dfa8ff';
    switch (title) {
      case 'High Alerts': return '#2B9EF0';
      case 'Categories': return '#FC9050';
      case 'Companies': return '#48B49B';
      case 'States': return '#9B86FF';
      default: return '#5525E9';
    }
  };

  return (
    <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {props.loading && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 2
        }}>
          <CircularProgress size={24} sx={{ color: getButtonColor(props.title) }} />
        </Box>
      )}
      <TableContainer
        component={Paper}
        sx={{
          position: 'relative',
          flex: 1,
          height: 'auto',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          borderRadius: 2,

        }}
      >
        <Table size="small" sx={{ fontSize: '0.85rem', backgroundColor: '#f7f7f7', }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: getHeaderColor(props.title) }}>
              <TableCell
                sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#333', }}
                sortDirection={orderBy === labelKey ? order : false}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{props.title}</span>
                  {props.title === 'Companies' && (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={companyType}
                        onChange={(e) => {
                          setCompanyType(e.target.value);
                          if (props.onCompanyTypeChange) {
                            props.onCompanyTypeChange(e.target.value);
                          }
                        }}
                        sx={{
                          fontSize: '0.75rem',
                          height: '24px',
                          backgroundColor: '#fff',
                          borderRadius: 1,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#ddd',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#999',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: getButtonColor(props.title),
                          },
                        }}
                      >
                        <MenuItem value="all" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>All</MenuItem>
                        <MenuItem value="converged" sx={{ fontSize: '0.75rem' }}>Convergence</MenuItem>
                        <MenuItem value="non_converged" sx={{ fontSize: '0.75rem' }}>Non-Convergence</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </TableCell>

              <TableCell
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  textAlign: 'right',
                  paddingRight: 2,
                  color: '#333',

                }}
                sortDirection={orderBy === valueKey ? order : false}
              >
                <TableSortLabel
                  active={orderBy === valueKey}
                  direction={orderBy === valueKey ? order : 'asc'}
                  onClick={() => handleSort(valueKey)}
                  sx={{
                    '&.MuiTableSortLabel-root': {
                      color: '#333',
                    },
                    '&.MuiTableSortLabel-root.Mui-active': {
                      color: '#333',
                    },
                    '& .MuiTableSortLabel-icon': {
                      color: '#333 !important',
                    },
                  }}
                >
                  {valueTitle}
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedData.slice(0, 5).map((row, idx) => (
              <TableRow
                key={idx}
                sx={{
                  '&:hover': { backgroundColor: '#f9f9f9' },
                  ...(sortedData.length > 5 && idx === 4 && { '& td': { borderBottom: 0, marginBottom: 0 } })
                }}
              >
                <TableCell
                  sx={{
                    fontSize: '0.85rem',
                    py: 1.1,
                    maxWidth: { xs: 120, sm: 160 },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',

                  }}
                >
                  {formatCompanyLabel(row, type)}
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: '0.85rem',
                    py: 1.1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1,
                    color: type === 'alert' ? getAlertColor(row[valueKey]) : 'text.primary',

                  }}
                >
                  {type === 'alert' && <TrendingUpIcon sx={{ fontSize: 14, flexShrink: 0 }} />}
                  <span>
                    {row[valueKey]}
                    {type === 'alert' && row[valueKey] !== 0 && '%'}
                  </span>
                </TableCell>
              </TableRow>
            ))}

            {/* See more button as last row */}
            {sortedData.length > 5 && !props.title?.startsWith('Company offers') && (
              <TableRow>
                <TableCell colSpan={2} sx={{ border: 0, py: 1, textAlign: 'right', pr: 2, }}>
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      fontSize: '0.7rem',
                      textTransform: 'none',
                      backgroundColor: "#fff",
                      color: getButtonColor(props.title),
                      border: `1px solid ${getHeaderColor(props.title)}`,
                      boxShadow: 'none',
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.25,
                      minWidth: 'auto',

                      '&:hover': {
                        borderColor: getButtonColor(props.title),
                        filter: 'brightness(0.9)',
                        boxShadow: 'none',
                      }
                    }}
                    onClick={() => {
                      if (props.title === 'High Alerts') {
                        navigate('/alert', { state: { autoFetch: true } });
                      } else if (props.title === 'Categories') {
                        navigate('/categories', { state: { autoFetch: true } });
                      } else if (props.title === 'States') {
                        navigate("/semantic-search", { state: { autoFetch: true } });
                      } else if (props.title === 'Companies') {
                        navigate("/companies", { state: { autoFetch: true } });
                      } else {
                        console.warn('Unhandled table title:', props.title);
                      }
                    }}
                  >
                    See more...
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {displayData.length === 0 && (
          <Box sx={{
            p: 2,
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '0.85rem',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',

          }}>
            {type === 'alert' ? (
              <>
                <div>No high alerts</div>
                <Link to="/alert" style={{ color: '#3585F5', textDecoration: 'none', marginTop: '8px', display: 'inline-block' }}>
                  View All alerts
                </Link>
              </>
            ) : (
              "No data available"
            )}
          </Box>
        )}



      </TableContainer>


    </Box>
  );
};

export default AlertTable;